import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { useWindowMouseDrag } from '~/hooks/useWindowMouseDrag';
import { resampleInt16Grid } from '~/domain/services/resampleGrid';
import {
    ReformatExtension,
    ReformatRegion,
    buildReformatGrid,
} from '~/domain/services/imageService';
import { formatMmAsCm } from '~/utils/units';
import type { ReformatSourceVolume } from '~/hooks/image/useReformatSourceVolume';

interface ReformatPreviewProps {
    /** Null until the source volume has been read. */
    source: ReformatSourceVolume | null;
    /** The source could not be read; there will be no preview. */
    failed: boolean;
    region: ReformatRegion;
    matrixSize: number;
    sliceThicknessMm: number;
    extension: ReformatExtension;
    /** {@link realisedZRangeMm} for the region being previewed. */
    realisedZRange: readonly [number, number];
    /** Slice centre to show, patient mm. */
    zMm: number;
    sizePx: number;
    /**
     * Drag across the picture, in patient mm on the two in-plane axes.
     *
     * Moving the centre and resizing about it are the two gestures the
     * region is edited with, and they are orthogonal: this is the first,
     * the wheel is the second.
     */
    onPan?: (dxMm: number, dyMm: number) => void;
    /** Wheel over the picture: +1 widens the field of view, -1 narrows it. */
    onZoom?: (direction: number) => void;
}

/**
 * One slice of the output, resampled exactly as the reformat will
 * resample it.
 *
 * Built through the same {@link buildReformatGrid} and `resampleInt16Grid`
 * as the reformat, with the same padding value, over a region whose slice
 * range has zero length — which `regionSliceCount` resolves to a single
 * slice. Nothing here re-implements the output geometry, so the picture
 * cannot drift from what the operation produces.
 *
 * A single slice is one `nz`th of the work — a few hundred thousand
 * samples instead of tens of millions — which is what makes this
 * affordable while the user is still dragging.
 */
export default function ReformatPreview({
    source,
    failed,
    region,
    matrixSize,
    sliceThicknessMm,
    extension,
    realisedZRange,
    zMm,
    sizePx,
    onPan,
    onZoom,
}: ReformatPreviewProps) {
    const { formatMessage } = useIntl();
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // The picture spans exactly the field of view, so this is the whole of
    // the screen-to-patient mapping: no offset, because what is drawn IS
    // the region.
    const mmPerPx = region.fovMm / sizePx;

    // Held in a ref so the window listeners stay stable while the value
    // they read stays current, as ProjectionCanvas does.
    const lastRef = useRef<{ x: number; y: number } | null>(null);
    const onDragMove = useCallback(
        (e: MouseEvent) => {
            const last = lastRef.current;
            if (!last) {
                return;
            }
            if (!onPan) {
                // Panning was disabled part way through the gesture. The
                // cursor keeps moving, so the origin has to keep up: left
                // where it was, the whole accumulated travel would land as
                // one jump if panning came back before the button was let
                // go.
                lastRef.current = { x: e.clientX, y: e.clientY };
                return;
            }
            onPan(
                (e.clientX - last.x) * mmPerPx,
                (e.clientY - last.y) * mmPerPx
            );
            lastRef.current = { x: e.clientX, y: e.clientY };
        },
        [onPan, mmPerPx]
    );
    const startDrag = useWindowMouseDrag(onDragMove, () => {
        lastRef.current = null;
    });

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            // Primary button only. A right-button press opens the context
            // menu, which can swallow the mouseup and leave the window
            // listener panning with nothing held down.
            if (e.button !== 0 || !onPan) {
                return;
            }
            lastRef.current = { x: e.clientX, y: e.clientY };
            startDrag(e);
        },
        [onPan, startDrag]
    );

    // Native non-passive listener, for the reason `NumberInput` gives.
    const hostRef = useRef<HTMLDivElement>(null);
    const zoomRef = useRef(onZoom);
    zoomRef.current = onZoom;
    useEffect(() => {
        const host = hostRef.current;
        if (!host) {
            return;
        }
        const onWheel = (event: WheelEvent) => {
            if (event.deltaY === 0 || !zoomRef.current) {
                return;
            }
            event.preventDefault();
            zoomRef.current(event.deltaY < 0 ? -1 : 1);
        };
        host.addEventListener('wheel', onWheel, { passive: false });
        return () => host.removeEventListener('wheel', onWheel);
    }, []);

    /**
     * Where to sample for the requested slice.
     *
     * Inside the extension the output holds a copy of the boundary slice,
     * so the preview samples the boundary too. Sampling the requested
     * position instead would show padding where the output will show
     * anatomy — the preview would misrepresent precisely the content it
     * exists to expose.
     */
    const sampled = useMemo(() => {
        const [low, high] = realisedZRange;
        if (extension.inferior && zMm < low) {
            return { zMm: low, replica: true };
        }
        if (extension.superior && zMm > high) {
            return { zMm: high, replica: true };
        }
        return { zMm, replica: false };
    }, [realisedZRange, extension, zMm]);

    // Coalesced onto an animation frame. A drag fires a mousemove far more
    // often than the browser paints, and a full slice resample per event
    // both wasted the work and left the canvas visibly behind the drag.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !source) {
            return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        const handle = requestAnimationFrame(() => {
            draw(ctx);
        });
        return () => cancelAnimationFrame(handle);

        function draw(ctx: CanvasRenderingContext2D) {
            if (!source) {
                return;
            }
            const grid = buildReformatGrid(
                { ...region, zRangeMm: [sampled.zMm, sampled.zMm] },
                matrixSize,
                sliceThicknessMm
            );
            const { pixels } = resampleInt16Grid(
                source.pixels,
                source.grid,
                grid,
                {
                    backgroundValue: source.paddingValue,
                }
            );

            // Windowed by the same rule as the scouts, so the same anatomy
            // reads the same way in both pictures.
            const [centre, width] = source.window;
            const windowMin = centre - width / 2;
            const scale = 255 / width;
            const rgba = new Uint8ClampedArray(matrixSize * matrixSize * 4);
            for (let i = 0, j = 0; i < pixels.length; i++, j += 4) {
                const v = (pixels[i] - windowMin) * scale;
                rgba[j] = v;
                rgba[j + 1] = v;
                rgba[j + 2] = v;
                rgba[j + 3] = 255;
            }

            const slice = document.createElement('canvas');
            slice.width = matrixSize;
            slice.height = matrixSize;
            slice
                .getContext('2d')
                ?.putImageData(
                    new ImageData(rgba, matrixSize, matrixSize),
                    0,
                    0
                );

            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(slice, 0, 0, sizePx, sizePx);
        }
    }, [source, region, matrixSize, sliceThicknessMm, sampled.zMm, sizePx]);

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={0.5}
        >
            <Box
                ref={hostRef}
                onMouseDown={handleMouseDown}
                sx={{
                    position: 'relative',
                    width: sizePx,
                    height: sizePx,
                    backgroundColor: '#000',
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: onPan ? 'move' : 'default',
                }}
            >
                <canvas
                    ref={canvasRef}
                    width={sizePx}
                    height={sizePx}
                    style={{
                        width: '100%',
                        height: '100%',
                        imageRendering: 'pixelated',
                    }}
                />
            </Box>
            <Typography
                variant="caption"
                color={
                    failed || sampled.replica
                        ? 'warning.main'
                        : 'text.secondary'
                }
            >
                {failed
                    ? formatMessage({ id: 'reformatPreviewFailed' })
                    : sampled.replica
                      ? formatMessage(
                            { id: 'reformatPreviewReplica' },
                            { z: formatMmAsCm(zMm, 1) }
                        )
                      : formatMessage(
                            { id: 'reformatPreviewAt' },
                            { z: formatMmAsCm(zMm, 1) }
                        )}
            </Typography>
        </Box>
    );
}
